import React, { useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Cpu,
  Wrench,
  Link,
  Bot,
  Search,
  Database,
  AlertCircle,
} from "lucide-react";
import { OTelSpan, SpanKind } from "./types";
import { TraceGroup } from "./utils";

interface TraceTreeProps {
  groups: TraceGroup[];
  selectedSpanId: string | null;
  onSelectSpan: (span: OTelSpan) => void;
}

const getSpanIcon = (kind?: SpanKind, status?: "OK" | "ERROR" | "UNSET") => {
  if (status === "ERROR")
    return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  switch (kind) {
    case "LLM":
      return <Cpu className="w-3.5 h-3.5 text-purple-400" />;
    case "TOOL":
      return <Wrench className="w-3.5 h-3.5 text-blue-400" />;
    case "CHAIN":
      return <Link className="w-3.5 h-3.5 text-success-400" />;
    case "AGENT":
      return <Bot className="w-3.5 h-3.5 text-orange-400" />;
    case "RETRIEVER":
      return <Search className="w-3.5 h-3.5 text-yellow-400" />;
    case "EMBEDDING":
      return <Database className="w-3.5 h-3.5 text-cyan-400" />;
    default:
      return (
        <div className="w-3 h-3 rounded-full border border-[#3E4044] bg-[#e2e8f0]" />
      );
  }
};

const formatDuration = (ms: number) => {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}µs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

interface TraceTreeNodeProps {
  span: OTelSpan;
  depth: number;
  selectedSpanId: string | null;
  onSelectSpan: (span: OTelSpan) => void;
  defaultExpanded?: boolean;
  isLastChild?: boolean;
  activeAncestors: boolean[];
}

const TraceTreeNode: React.FC<TraceTreeNodeProps> = ({
  span,
  depth,
  selectedSpanId,
  onSelectSpan,
  defaultExpanded = true,
  isLastChild = false,
  activeAncestors,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const hasChildren = span.children && span.children.length > 0;
  const isSelected = span.id === selectedSpanId;
  const kind = span.attributes["openinference.span.kind"];
  // Detect pydantic-ai tool spans by gen_ai.tool.name attribute
  const effectiveKind: SpanKind | undefined =
    kind ?? (span.attributes["gen_ai.tool.name"] ? "TOOL" : undefined);
  const status = span.status.code;
  const tokens =
    span.attributes["llm.token_count.total"] ??
    (typeof span.attributes["gen_ai.usage.input_tokens"] === "number" &&
    typeof span.attributes["gen_ai.usage.output_tokens"] === "number"
      ? (span.attributes["gen_ai.usage.input_tokens"] as number) +
        (span.attributes["gen_ai.usage.output_tokens"] as number)
      : undefined);

  return (
    <div className="relative">
      <div
        data-testid="trace-span-row"
        data-span-id={span.id}
        data-span-name={span.name}
        data-span-kind={effectiveKind ?? "UNKNOWN"}
        data-span-status={status}
        data-span-depth={depth}
        className={`flex items-center group cursor-pointer py-1.5 pr-3 text-sm select-none transition-colors relative z-10
          ${isSelected ? "bg-[#e2e8f0] text-[#0f172a]" : "text-[#64748b] hover:bg-[#f8fafc] hover:text-[#0f172a]"}
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelectSpan(span)}
      >
        {/* Ancestor Lines */}
        {activeAncestors.map((isActive, i) =>
          isActive ? (
            <div
              key={i}
              className="absolute w-px h-full bg-[#e2e8f0] z-0"
              style={{ left: `${i * 16 + 15}px`, top: 0 }}
            />
          ) : null
        )}

        {/* Current Node L-Shape */}
        {depth > 0 && (
          <>
            <div
              className="absolute w-px bg-[#e2e8f0] z-0"
              style={{
                left: `${(depth - 1) * 16 + 15}px`,
                top: 0,
                height: isLastChild ? "16px" : "100%",
              }}
            />
            <div
              className="absolute h-px bg-[#e2e8f0] z-0"
              style={{
                left: `${(depth - 1) * 16 + 15}px`,
                width: "8px",
                top: "16px",
              }}
            />
          </>
        )}

        {/* Expand/Collapse Toggle */}
        <div
          data-testid="trace-span-toggle"
          data-expanded={hasChildren ? isExpanded : undefined}
          className="w-4 h-4 flex items-center justify-center mr-1 text-[#94a3b8] hover:text-[#0f172a] bg-transparent relative z-10"
          onClick={(e) => {
            if (hasChildren) {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )
          ) : (
            <div className="w-3.5 h-3.5" /> // Spacer for alignment
          )}
        </div>

        {/* Icon */}
        <div className="mr-2 flex items-center justify-center relative z-10">
          {getSpanIcon(effectiveKind, status)}
        </div>

        {/* Name */}
        <div className="flex-1 truncate font-medium relative z-10">
          {span.name}
        </div>

        {/* Metrics (Right aligned, visible on hover or selected) */}
        <div
          className={`flex items-center gap-3 text-xs relative z-10 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}
        >
          {tokens && (
            <div className="flex items-center text-[#64748b]">
              <span className="font-mono">{tokens}</span>
              <span className="ml-1 text-[10px] uppercase tracking-wider opacity-60">
                tok
              </span>
            </div>
          )}
          <div className="text-[#64748b] font-mono w-12 text-right">
            {formatDuration(span.durationMs)}
          </div>
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="flex flex-col relative">
          {span.children!.map((child, index) => (
            <TraceTreeNode
              key={child.id}
              span={child}
              depth={depth + 1}
              selectedSpanId={selectedSpanId}
              onSelectSpan={onSelectSpan}
              isLastChild={index === span.children!.length - 1}
              activeAncestors={[...activeAncestors, !isLastChild]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const formatGroupTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const TraceTree: React.FC<TraceTreeProps> = ({
  groups,
  selectedSpanId,
  onSelectSpan,
}) => {
  // Iteration chips only matter when a node executed more than once.
  const nodeGroupCounts = new Map<string, number>();
  for (const group of groups) {
    if (group.nodeId) {
      nodeGroupCounts.set(
        group.nodeId,
        (nodeGroupCounts.get(group.nodeId) ?? 0) + 1
      );
    }
  }

  return (
    <div className="flex flex-col w-full h-full bg-white overflow-y-auto py-2">
      {groups.map((group, groupIndex) => (
        <div key={group.traceId} className="flex flex-col">
          {groups.length > 1 && (
            <div
              data-testid="trace-group-header"
              className="flex items-center gap-2 px-3 pt-3 pb-1 select-none"
            >
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#94a3b8]">
                Trace {groupIndex + 1}
              </span>
              {group.nodeIteration !== undefined &&
                (nodeGroupCounts.get(group.nodeId ?? "") ?? 0) > 1 && (
                  <span className="px-1 py-px text-[10px] font-mono rounded bg-[#f1f5f9] text-[#64748b]">
                    it {group.nodeIteration}
                  </span>
                )}
              <span className="text-[10px] font-mono text-[#94a3b8]">
                {formatGroupTime(group.startTime)}
              </span>
              <div className="flex-1 h-px bg-[#e2e8f0]" />
            </div>
          )}
          {group.roots.map((span, index) => (
            <TraceTreeNode
              key={span.id}
              span={span}
              depth={0}
              selectedSpanId={selectedSpanId}
              onSelectSpan={onSelectSpan}
              isLastChild={index === group.roots.length - 1}
              activeAncestors={[]}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
